import * as React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { columnsNoAggregation, columnsAggregatedDay, columnsAggregatedWeek, columnsAggregatedMonth, columnsAggregatedYear } from '../internals/data/gridData';
import LoadingData from './LoadingData';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

interface DataGridProps {
  data: any[];
  loading: boolean;
  aggregation: string;
}

const getRowId = (row: any) => row.reading_time;

const ConsumptionDataGrid: React.FC<DataGridProps> = ({ data, loading, aggregation }) => {  
  const theme = useTheme();
  
  // Create theme-aware column definitions
  const getThemeAwareColumns = () => {
    const isDarkMode = theme.palette.mode === 'dark';

    // Helper function to create theme-aware renderCell functions
    const createThemeAwareRenderCell = (colorType: string, min: number, max: number) => {
      return (params: any) => {
        // If the raw value is null/undefined, display the formatted value (valueFormatter may supply 'No data')
        if (params.value === null || params.value === undefined) {
          return <div style={{ width: '100%' }}>{params.formattedValue ?? 'No data'}</div>;
        }

        // Ensure we have a numeric value for shading. If parsing fails fall back to 0 for shading
        const numericValue = typeof params.value === 'number' ? params.value : parseFloat(String(params.value));
        const valueForShade = isNaN(numericValue) ? 0 : numericValue;

        const backgroundColor = getShade(colorType, min, max, valueForShade, isDarkMode);
        return (
          <div style={{ 
            backgroundColor,
            width: '100%', 
          }}>
            {params.formattedValue}
          </div>
        );
      };
    };

    // Get base columns
    let baseColumns = columnsNoAggregation;
    switch (aggregation) {
      case 'day':
        baseColumns = columnsAggregatedDay;
        break;
      case 'week':
        baseColumns = columnsAggregatedWeek;
        break;
      case 'month':
        baseColumns = columnsAggregatedMonth;
        break;
      case 'year':
        baseColumns = columnsAggregatedYear;
        break;
    }

    // Create theme-aware columns with updated renderCell functions
    return baseColumns.map(col => {
      if (col.field === 'consumption') {
        return {
          ...col,
          renderCell: createThemeAwareRenderCell('consumption', 0, 
            aggregation === 'none' ? 1 :
             (aggregation === 'hour' ? 2 : 
              (aggregation === 'day' ? 15 : 
                (aggregation === 'week' ? 80 : 
                  (aggregation === 'month' ? 300 : 5000)))))
        };
      } else if (col.field === 'cost_inc_vat') {
        return {
          ...col,
          renderCell: createThemeAwareRenderCell('cost', 0, 
            aggregation === 'none' ? 10 :
              (aggregation === 'hour' ? 20 : 
                (aggregation === 'day' ? 1.5 : 
                  (aggregation === 'week' ? 10 : 
                    (aggregation === 'month' ? 60 : 500)))))
        };
      } else if (col.field === 'gCO_2') {
        return {
          ...col,
          renderCell: createThemeAwareRenderCell('emissions', 0, 
            aggregation === 'none' ? 30 : 
              (aggregation === 'hour' ? 60 : 
                (aggregation === 'day' ? 2 : 
                  (aggregation === 'week' ? 5 : 
                    (aggregation === 'month' ? 20 : 200)))))
        };
      }
      return col;
    });
  };

  const columns = getThemeAwareColumns();

  // Helper function to get shade color using alpha channel transparency
  const getShade = (colorType: string, min: number, max: number, value: number, isDarkMode: boolean = false) => {
    // Handle case where max is 0 to avoid division by zero
    if (max === 0) {
      max = 1; // Set to 1 to avoid division by zero, but this will result in no shading
    }
    
    // Calculate alpha based on value relative to maximum
    // Alpha will be 0 (transparent) when value is 0, and 1 (fully opaque) when value equals max
    const alpha = value / max;
    
    if (isDarkMode) {
      // Dark mode: use chart colors with alpha transparency
      if (colorType === 'consumption') {
        // Consumption: green (#4caf50) with alpha
        return `rgba(76, 175, 80, ${alpha})`; // Green with alpha
      } else if (colorType === 'cost') {
        // Cost: blue (#1976d2) with alpha (swapped from purple)
        return `rgba(25, 118, 210, ${alpha})`; // Blue with alpha
      } else if (colorType === 'emissions') {
        // Emissions: brown (#67330eff) with alpha
        return `rgba(103, 51, 14, ${alpha})`; // Brown with alpha
      }
    } else {
      // Light mode: use chart colors with alpha transparency
      if (colorType === 'consumption') {
        // Consumption: green (#4caf50) with alpha
        return `rgba(76, 175, 80, ${alpha})`; // Green with alpha
      } else if (colorType === 'cost') {
        // Cost: blue (#1976d2) with alpha (swapped from purple)
        return `rgba(25, 118, 210, ${alpha})`; // Blue with alpha
      } else if (colorType === 'emissions') {
        // Emissions: brown (#714627ff) with alpha
        return `rgba(113, 70, 39, ${alpha})`; // Brown with alpha
      }
    }
    
    // Fallback color if colorType is not recognized
    return `rgba(128, 128, 128, ${alpha})`; // Grey with alpha
  };

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <LoadingData open={loading} />
      <DataGrid
        sx={{
          // Disable MUI DataGrid's mobile-specific behavior
          "& .MuiDataGrid-root": {
            overflowX: "hidden !important"
          },
          "& .MuiDataGrid-main": {
            overflowX: "hidden !important"
          },
          "& .MuiDataGrid-virtualScroller": {
            overflowX: "hidden !important"
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            whiteSpace: "normal",
            lineHeight: "normal"
          },
          "& .MuiDataGrid-columnHeader": {
            // Forced to use important since overriding inline styles
            height: "unset !important"
          },
          position: 'relative',
          // Remove border radius on mobile
          [theme.breakpoints.down('md')]: {
            borderRadius: 0,
            '& .MuiDataGrid-main': {
              borderRadius: 0,
            },
            '& .MuiDataGrid-container--top': {
              borderRadius: 0,
            },
            '& .MuiDataGrid-container--bottom': {
              borderRadius: 0,
            },
            '& .MuiDataGrid-columnHeaders': {
              borderRadius: 0,
            },
            '& .MuiDataGrid-footerContainer': {
              borderRadius: 0,
            },
          },
        }}
        autoHeight
        rows={data}
        columns={columns}
        rowHeight={40}
        getRowId={getRowId}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        disableColumnResize
        density="compact"
        hideFooter
        slotProps={{
          filterPanel: {
            filterFormProps: {
              logicOperatorInputProps: {
                variant: 'outlined',
                size: 'small',
              },
              columnInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              operatorInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              valueInputProps: {
                InputComponentProps: {
                  variant: 'outlined',
                  size: 'small',
                },
              },
            },
          },
        }}
      />
    </Box>
  );
};

export default ConsumptionDataGrid;